'use client';

import { CharacterCounter, Icon } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import { Input } from '@workspace/ui/components/input';
import { Textarea } from '@workspace/ui/components/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageLoader from '@/src/components/ui/PageLoader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { ProgressBar } from '@/src/components/ui/ProgressBar';
import {
  useDonationGoal,
  useDonationGoalMutations,
} from '@/src/hooks/use-donation-goal';
import { useProfile } from '@/src/hooks/use-profile';
import { useEffect, useState } from 'react';
import { IDonationGoal } from 'types/src/types';
import { Confetti } from '@/src/components/layout/Confetti';

const donationGoalSchema = z.object({
  name: z
    .string()
    .min(1, 'Goal name is required')
    .nonempty('Goal name is required'),
  amount: z.number().min(1, 'Amount must be greater than 0'),
  description: z
    .string()
    .max(500, 'Description cannot exceed 500 characters')
    .nonempty('Description is required'),
});
type DonationGoalFormValues = z.infer<typeof donationGoalSchema>;

export const DonationGoal = () => {
  const { data: profile } = useProfile();
  const { data: goal, isLoading: isFetching } = useDonationGoal(
    profile?.id || -1,
  );
  const { createMutation, updateMutation, deleteMutation } =
    useDonationGoalMutations(profile?.id || -1);
  const isOpenDialog = useBoolean(false);
  const isLoading = useBoolean(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
    clearErrors,
  } = useForm<DonationGoalFormValues>({
    resolver: zodResolver(donationGoalSchema),
    defaultValues: {
      name: goal?.name || '',
      amount: goal?.targetAmount || undefined,
      description: goal?.description || '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (goal) {
      reset({
        name: goal.name || '',
        amount: goal.targetAmount || undefined,
        description: goal.description || '',
      });
    } else {
      reset({
        name: '',
        amount: undefined,
        description: '',
      });
    }
  }, [goal, reset]);

  const descriptionValue = watch('description');
  const amountValue = watch('amount');

  const onSubmit = async (data: DonationGoalFormValues) => {
    isLoading.onTrue();
    try {
      const donationData = {
        name: data.name,
        targetAmount: data.amount,
        description: data.description,
      };
      if (goal) {
        await updateMutation.mutateAsync({ id: goal.id, data: donationData });
      } else {
        await createMutation.mutateAsync(donationData);
      }
      isOpenDialog.onFalse();
      reset();
      clearErrors();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      isLoading.onFalse();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      return;
    }
  };

  const handleDelete = async () => {
    if (goal) {
      isLoading.onTrue();
      try {
        await deleteMutation.mutateAsync(goal.id);
        isOpenDialog.onFalse();
        reset();
        clearErrors();
      } catch (error) {
        console.error('Error deleting goal:', error);
      } finally {
        isLoading.onFalse();
      }
    }
  };

  const handleClose = () => {
    isOpenDialog.onFalse();
    reset();
    clearErrors();
  };

  const isAmountValid = !goal || goal.targetAmount >= amountValue;

  const convertedDate = goal
    ? new Date(goal.startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <>
      {isLoading.value && <PageLoader />}
      {!goal ? (
        <div className="bg-bg-gray md:p-5 p-3 rounded-[12px] md:w-1/2 w-full md:mb-8">
          <h4 className="text-sm md:text-2xl font-semibold text-black/80">
            Donation goal
          </h4>
          <p className="md:text-sm text-[10px] text-black/60 md:mb-9 mb-3 font-light">
            There is no active donation goal now
          </p>
          <Button
            className="w-full md:text-sm font-medium py-1 h-[30px]"
            onClick={isOpenDialog.onTrue}
            disabled={!profile?.payment.provider || isLoading.value}
          >
            Create goal
          </Button>
        </div>
      ) : (
        <div className="bg-bg-gray p-1 rounded-[12px] md:w-1/2 w-full md:mb-8 flex md:flex-row flex-col justify-between">
          <div className="md:p-4 p-2">
            <h4 className="text-sm md:text-2xl font-semibold text-black/80">
              Donation goal
            </h4>
            <p className="md:text-sm text-[10px] text-black/60 md:mb-9 mb-3 font-light">
              From {convertedDate}
            </p>
          </div>
          <div className="p-3 flex flex-col gap-3 bg-white rounded-lg md:max-w-[390px] w-full">
            <div className="flex justify-between">
              <h4 className="text-lg font-medium">{goal.name}</h4>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Icon name="dots" className="size-4 rotate-90" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="sm:w-fit">
                  <DropdownMenuItem
                    onClick={isOpenDialog.onTrue}
                    className="hover:cursor-pointer text-xs font-medium"
                  >
                    <Icon name="edit" className="size-3.5" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="hover:cursor-pointer text-xs font-medium"
                  >
                    <Icon name="delete" className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex flex-col gap-3">
              {goal.currentAmount >= goal.targetAmount && <Confetti />}
              <ProgressBar
                currentAmount={goal.currentAmount}
                targetAmount={goal.targetAmount}
              />
              <div className="flex justify-between items-end">
                <span className="md:text-xl font-normal text-blue-primary">
                  {Math.round((goal.currentAmount / goal.targetAmount) * 100)}%
                </span>
                <span className="text-base font-light text-black/80">
                  ${goal.currentAmount} / ${goal.targetAmount}
                </span>
              </div>
            </div>
            <div className="border-t border-bg-gray pt-2">
              <p className="md:text-sm text-[10px] text-black/60 font-light whitespace-pre-line">
                {goal.description}
              </p>
            </div>
          </div>
        </div>
      )}
      <Dialog
        open={isOpenDialog.value}
        onOpenChange={(open: boolean) => {
          if (open) {
            isOpenDialog.onTrue();
          } else {
            isOpenDialog.onFalse();
            reset();
            clearErrors();
          }
        }}
      >
        <DialogContent className="p-5 md:min-w-[540px] bg-white rounded-2xl gap-0">
          <DialogHeader className="mb-10">
            <DialogTitle>
              <div className="text-2xl font-semibold">
                {goal ? 'Edit a donation goal' : 'Add a donation goal'}
              </div>
            </DialogTitle>
            <DialogDescription className="text-sm max-w-[372px] font-normal">
              {goal
                ? 'Modify your existing donation goal. You can adjust the name and description freely, but the amount can only be reduced.'
                : 'Let your fans know what you are raising money for. You can change or remove your goal at any time.'}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-2">
              <label
                htmlFor="goalName"
                className="font-normal md:text-base text-sm text-black/80"
              >
                Name
              </label>
              <Input
                id="goalName"
                className="h-12 md:max-w-[500px] md:text-base! text-sm!"
                placeholder="Enter goal name"
                {...register('name')}
              />
              <span className="text-red-error text-sm min-h-[1rem] ml-3">
                {errors.name?.message}
              </span>
            </div>
            <div className="flex flex-col gap-2 relative">
              <label
                htmlFor="goalAmount"
                className="font-normal md:text-base text-sm text-black/80"
              >
                Goal amount
              </label>
              <Input
                id="goalAmount"
                type="number"
                className="h-12 md:max-w-[500px] md:text-base! text-sm!"
                placeholder="Set a total goal amount"
                {...register('amount', {
                  valueAsNumber: true,
                  required: true,
                  validate: () => isAmountValid || 'Amount can only be reduced',
                })}
              />
              <span className="absolute md:right-8 right-4 md:top-[46px] top-11 text-sm text-black/60">
                USD
              </span>
              <span className="text-red-error text-sm min-h-[1rem] ml-3">
                {errors.amount?.message}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="goalDescription"
                className="flex flex-col font-normal md:text-base text-sm text-black/80"
              >
                Goal description
                <div className="flex justify-between w-full">
                  <span className="flex-1 font-light md:text-sm text-xs mt-0.5 text-black/60 sm:w-fit w-full">
                    This description is visible to users.
                  </span>
                  <CharacterCounter
                    className="sm:w-fit max-w-[100px]"
                    maxLength={500}
                    value={descriptionValue || ''}
                  />
                </div>
              </label>
              <Textarea
                id="goalDescription"
                className="h-24 resize-none md:max-w-[500px] md:text-base! text-sm!"
                placeholder="Enter the description"
                {...register('description')}
                onKeyDown={handleKeyDown}
              />
              <span className="text-red-error text-sm min-h-[1rem] ml-3">
                {errors.description?.message}
              </span>
            </div>
            <hr className="border-bg-gray mb-5" />
            <DialogFooter className="flex sm:justify-between w-full">
              <div className="flex gap-2 md:flex-row flex-col">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  size="sm"
                  className="text-sm bg-transparent px-4"
                >
                  Close
                </Button>
                {goal && (
                  <Button
                    variant="outline"
                    onClick={handleDelete}
                    size="sm"
                    className="text-sm px-4 md:ml-2 bg-transparent text-red-error"
                  >
                    Delete goal
                  </Button>
                )}
              </div>
              <Button
                type="submit"
                size="sm"
                className="text-sm px-4"
                disabled={!isValid || !isAmountValid}
              >
                {goal ? 'Save changes' : 'Add goal'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

type ProfileDonationGoalProps = {
  goal: IDonationGoal;
  children?: React.ReactNode;
};

const MAX_DESCRIPTION_LENGTH = 100;

export const ProfileDonationGoal = ({
  goal,
  children,
}: ProfileDonationGoalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLongDescription = goal.description.length > MAX_DESCRIPTION_LENGTH;
  const truncatedDescription = isLongDescription
    ? `${goal.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
    : goal.description;

  return (
    <div className="border p-3 rounded-[12px] bg-white">
      <div className="flex justify-between mb-4">
        <h4 className="text-lg font-medium md:max-w-[300px]">{goal.name}</h4>
        {children}
      </div>
      <ProgressBar
        currentAmount={goal.currentAmount}
        targetAmount={goal.targetAmount}
        isProfile
      />
      <div className="border-t border-bg-gray pt-2 mt-3">
        {isLongDescription ? (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex items-center gap-2">
              <p className="md:text-sm text-[10px] text-black/60 font-light flex-1">
                {isOpen ? goal.description : truncatedDescription}
              </p>
              <CollapsibleTrigger asChild>
                <button
                  className="text-black/60 hover:text-black transition-transform duration-200"
                  aria-label={isOpen ? 'Show less' : 'Show more'}
                >
                  <Icon
                    name="chevron-down"
                    className={`size-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="CollapsibleContent"></CollapsibleContent>
          </Collapsible>
        ) : (
          <p className="md:text-sm text-[10px] text-black/60 font-light">
            {goal.description}
          </p>
        )}
      </div>
    </div>
  );
};
